import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { fetchBrands } from '../../util/api';
import { brandsFromApiResponse } from '../../util/brands';
import css from './BrandsBanner.module.css';

const AUTO_DURATION_DESKTOP_S = 40;
const AUTO_DURATION_MOBILE_S = 28;
const WHEEL_IDLE_MS = 900;
const MAX_FRAME_DELTA_S = 0.05;

const BrandsBanner = () => {
  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const loopWidthRef = useRef(0);
  const pixelsPerSecondRef = useRef(0);
  const lastTsRef = useRef(null);
  const lastUserWheelRef = useRef(0);
  const pausedHoverRef = useRef(false);
  const reduceMotionRef = useRef(false);
  const [brandsData, setBrandsData] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetchBrands()
      .then(response => {
        if (cancelled) return;
        setBrandsData(brandsFromApiResponse(response));
      })
      .catch(() => {
        if (cancelled) return;
        setBrandsData([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const brandsWithLogo = brandsData.filter(b => b.logo_url);
  const displayBrands = [...brandsWithLogo, ...brandsWithLogo];

  const updateMetrics = () => {
    const track = trackRef.current;
    if (!track || track.scrollWidth <= 0) {
      return;
    }
    const loopW = track.scrollWidth / 2;
    loopWidthRef.current = loopW;
    const isMobile =
      typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
    const duration = isMobile ? AUTO_DURATION_MOBILE_S : AUTO_DURATION_DESKTOP_S;
    pixelsPerSecondRef.current = loopW > 0 ? loopW / duration : 0;
  };

  const normalizeScroll = (el, loopW) => {
    if (!el || loopW <= 0) {
      return;
    }
    let s = el.scrollLeft;
    while (s >= loopW) {
      s -= loopW;
    }
    while (s < 0) {
      s += loopW;
    }
    el.scrollLeft = s;
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) {
      return undefined;
    }

    updateMetrics();
    const ro = new ResizeObserver(() => {
      updateMetrics();
    });
    ro.observe(track);
    return () => ro.disconnect();
  }, [brandsWithLogo.length]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const setReduce = () => {
      reduceMotionRef.current = mq.matches;
    };
    setReduce();
    mq.addEventListener('change', setReduce);
    return () => mq.removeEventListener('change', setReduce);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onBp = () => updateMetrics();
    mq.addEventListener('change', onBp);
    return () => mq.removeEventListener('change', onBp);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return undefined;
    }

    const onWheel = e => {
      const loopW = loopWidthRef.current;
      if (!loopW) {
        return;
      }
      const delta = e.deltaY + e.deltaX;
      if (delta === 0) {
        return;
      }
      e.preventDefault();
      lastUserWheelRef.current = Date.now();
      el.scrollLeft += delta;
      normalizeScroll(el, loopW);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    let rafId = 0;
    const tick = ts => {
      rafId = requestAnimationFrame(tick);
      const el = scrollRef.current;
      const loopW = loopWidthRef.current;
      if (!el || loopW <= 0) {
        lastTsRef.current = ts;
        return;
      }

      const reduceMotion = reduceMotionRef.current;
      const pausedHover = pausedHoverRef.current;
      const userRecentlyWheeled = Date.now() - lastUserWheelRef.current < WHEEL_IDLE_MS;

      const last = lastTsRef.current;
      lastTsRef.current = ts;
      const rawDt = last != null ? (ts - last) / 1000 : 0;
      const dt = Math.min(Math.max(rawDt, 0), MAX_FRAME_DELTA_S);

      const runAuto = !reduceMotion && !pausedHover && !userRecentlyWheeled;
      if (runAuto && dt > 0) {
        el.scrollLeft += pixelsPerSecondRef.current * dt;
        normalizeScroll(el, loopW);
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  if (brandsWithLogo.length === 0) {
    return null;
  }

  return (
    <section className={css.root}>
      <div className={css.header}>
        <span className={css.label}>Nos Marques Partenaires</span>
      </div>

      <div
        className={css.marqueeOuter}
        onMouseEnter={() => {
          pausedHoverRef.current = true;
        }}
        onMouseLeave={() => {
          pausedHoverRef.current = false;
        }}
      >
        <div className={css.fadeLeft} aria-hidden="true" />
        <div className={css.fadeRight} aria-hidden="true" />

        <div ref={scrollRef} className={css.marqueeScroll}>
          <div ref={trackRef} className={css.track} aria-hidden="false">
            {displayBrands.map((brand, index) => {
              const itemClass = brand.logoOnDark
                ? `${css.brandItem} ${css.brandItemDark}`
                : css.brandItem;
              const isMirrorItem = index >= brandsWithLogo.length;
              return isMirrorItem ? (
                <div key={`${brand.value}-${index}`} className={itemClass} aria-hidden="true">
                  <img
                    src={brand.logo_url}
                    alt=""
                    className={css.brandLogo}
                    loading="lazy"
                    draggable={false}
                  />
                </div>
              ) : (
                <Link
                  key={`${brand.value}-${index}`}
                  to={`/products/${brand.value}`}
                  className={itemClass}
                  title={brand.brand}
                  aria-label={`Voir les produits ${brand.brand}`}
                >
                  <img
                    src={brand.logo_url}
                    alt={brand.brand}
                    className={css.brandLogo}
                    loading="lazy"
                    draggable={false}
                  />
                  <span className={css.srOnly}>{brand.brand}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandsBanner;
